import {
  assassinCommand,
  autoDefendCommand,
  guardCommand,
  royalCommand,
  soldierCommand,
  trainingCommand,
  tickAiMemory,
} from './ai';
import { bodyguardPosts, guardPosts, musterPosition, thronePosition } from './castle';
import {
  arrowBlockedAt,
  baileyPlots,
  buildingPlots,
  enforceLevel,
  plotOf,
  resolveObstacles,
  settleMove,
  syncBuildingObstacles,
} from './world';
import {
  bankingBonus,
  defenseMultiplier,
  dodgeCostMultiplier,
  hasTech,
  isTechAvailable,
  offenseMultiplier,
  recomputeIncome,
  spawnHealthMultiplier,
  techById,
  waveUnlocked,
} from './tech';
import {
  attackSpec,
  DODGE_DURATION,
  DODGE_SPEED_MULTIPLIER,
  DODGE_STAMINA_COST,
  FEINT_STAMINA_COST,
  FEINT_WINDOW_FRACTION,
} from './attacks';
import type { AttackKind } from './attacks';
import {
  canSprint,
  isInsideArc,
  receiveHit,
  resetCombatState,
  tickStamina,
  trySpendStamina,
} from './combat';
import { PROFILES } from './profiles';
import type { ProfileId } from './profiles';
import { applySeparation } from './spatial';
import { enemyPosturePolicy, tickCommander } from './commander';
import { makeSquad, SQUADS_PER_SIDE, SQUAD_SIZE, squadLeader, tickSquad } from './squads';
import type {
  Archetype,
  Brain,
  BuildingKind,
  CharacterId,
  CharacterKind,
  CharacterState,
  Command,
  GameState,
  PurchaseKind,
  Team,
} from './types';
import { EMPTY_COMMAND } from './types';
import { receiveArrowHit } from './combat';

export { TICK_RATE, DT } from './constants';
import { ARROW_GRAVITY, ARROW_LAUNCH_H, ARROW_SPEED, TICK_RATE, DT } from './constants';

// Playable area (492 x 300).
export const WORLD_HALF_X = 246;
export const WORLD_HALF_Z = 150;

/** Character body radius used for melee hitbox overlap. */
const BODY_RADIUS = 0.45;
const SPRINT_MULTIPLIER = 1.45;
const RESPAWN_SECONDS = 5;
const ASSASSIN_RESPAWN_SECONDS = 20;
/** How often each commander reassesses the battlefield, in ticks. */
const COMMANDER_PERIOD = 90;

// Economy
const GOLD_START = 150;
const GOLD_CAP = 2000;
const BASE_INCOME = 3;
/** Base army cap; each completed Housing adds 10 (up to two). */
const POP_BASE = 40;
/** Each additional building of a kind costs more. */
const BUILDING_COST_GROWTH = 1.35;
/** Seconds to raise a building. */
const BUILD_SECONDS = 20;
/** Bailey singletons; housing is capped separately. */
const SINGLETON_BUILDINGS: BuildingKind[] = ['smithy', 'range', 'chapel', 'treasury'];
const HOUSING_MAX = 2;

export function popCap(state: GameState, team: Team): number {
  let cap = POP_BASE;
  for (const b of state.buildings) {
    if (b.team === team && b.kind === 'housing' && b.progress >= 1) cap += 10;
  }
  return Math.min(cap, POP_BASE + HOUSING_MAX * 10);
}

export interface ShopWaveItem {
  kind: 'wave';
  label: string;
  cost: number;
  count: number;
  archetype: Archetype;
  profile: ProfileId;
  maxHealth: number;
  baseSpeed: number;
}
export interface ShopBuildingItem {
  kind: 'building';
  label: string;
  cost: number;
  building: BuildingKind;
  income: number;
  /** Field plots sit outside the walls; bailey plots inside the courtyard. */
  where: 'field' | 'bailey';
  maxHp: number;
  desc: string;
}
export type ShopItem = ShopWaveItem | ShopBuildingItem;

export const SHOP: Record<PurchaseKind, ShopItem> = {
  infantry: {
    kind: 'wave',
    label: 'Infantry ×6',
    cost: 120,
    count: 6,
    archetype: 'infantry',
    profile: 'soldier',
    maxHealth: 90,
    baseSpeed: 3.3,
  },
  archers: {
    kind: 'wave',
    label: 'Archers ×4',
    cost: 200,
    count: 4,
    archetype: 'archer',
    profile: 'soldier',
    maxHealth: 60,
    baseSpeed: 3.4,
  },
  knights: {
    kind: 'wave',
    label: 'Knights ×3',
    cost: 260,
    count: 3,
    archetype: 'knight',
    profile: 'veteranSoldier',
    maxHealth: 140,
    baseSpeed: 3.05,
  },
  farm: { kind: 'building', label: 'Farm', cost: 150, building: 'farm', income: 2, where: 'field', maxHp: 120, desc: '+2 gold/s, outside the walls' },
  market: { kind: 'building', label: 'Market', cost: 300, building: 'market', income: 4, where: 'field', maxHp: 140, desc: '+4 gold/s, outside the walls' },
  smithy: { kind: 'building', label: 'Smithy', cost: 200, building: 'smithy', income: 0, where: 'bailey', maxHp: 220, desc: 'Opens the military tech branch' },
  range: { kind: 'building', label: 'Archery Range', cost: 250, building: 'range', income: 0, where: 'bailey', maxHp: 200, desc: 'Unlocks archer waves + longbow tech' },
  chapel: { kind: 'building', label: 'Chapel', cost: 250, building: 'chapel', income: 0, where: 'bailey', maxHp: 200, desc: 'Opens the royal tech branch' },
  treasury: { kind: 'building', label: 'Treasury', cost: 300, building: 'treasury', income: 0, where: 'bailey', maxHp: 240, desc: 'Enables Banking research' },
  housing: { kind: 'building', label: 'Housing', cost: 180, building: 'housing', income: 0, where: 'bailey', maxHp: 160, desc: '+10 army cap (max two)' },
};

export function buildingCost(state: GameState, team: Team, kind: BuildingKind): number {
  const item = SHOP[kind] as ShopBuildingItem;
  const count = state.buildings.filter((b) => b.team === team && b.kind === kind).length;
  return Math.round(item.cost * Math.pow(BUILDING_COST_GROWTH, count));
}

export function alivePop(state: GameState, team: Team): number {
  let n = 0;
  for (const c of Object.values(state.characters)) {
    if (c.alive && c.brain === 'soldier' && c.team === team) n++;
  }
  return n;
}

/** Player avatar and practice dummy come back quickly; the enemy assassin
 * returns after a longer delay; soldiers are battle attrition (revived only by
 * paid reinforcements); Kings never respawn — their death decides the match. */
function scheduleRespawn(state: GameState, c: CharacterState): void {
  if (c.alive || c.respawnAtTick !== null || c.kind === 'king') return;
  if (c.brain === 'assassin') {
    c.respawnAtTick = state.tick + ASSASSIN_RESPAWN_SECONDS * TICK_RATE;
  } else if (c.kind === 'agent' || c.kind === 'training') {
    c.respawnAtTick = state.tick + RESPAWN_SECONDS * TICK_RATE;
  }
}

interface SpawnSpec {
  id: CharacterId;
  kind: CharacterKind;
  team: Team;
  brain: Brain;
  profile: ProfileId;
  x: number;
  z: number;
  /** Spawn elevation (wall archers start up on the battlements). */
  elev?: number;
  heading: number;
  maxHealth: number;
  baseSpeed: number;
  squadId?: string;
  squadIndex?: number;
  archetype?: Archetype;
  role?: string;
  patrolRoute?: string;
  routeStart?: number;
}

function makeCharacter(s: SpawnSpec): CharacterState {
  return {
    id: s.id,
    kind: s.kind,
    team: s.team,
    brain: s.brain,
    profile: s.profile,
    x: s.x,
    z: s.z,
    elev: s.elev ?? 0,
    safeX: s.x,
    safeZ: s.z,
    heading: s.heading,
    spawnX: s.x,
    spawnZ: s.z,
    spawnElev: s.elev ?? 0,
    health: s.maxHealth,
    maxHealth: s.maxHealth,
    baseSpeed: s.baseSpeed,
    alive: true,
    moving: false,
    sprinting: false,
    stamina: PROFILES[s.profile].maximumStamina,
    exhausted: false,
    regenDelay: 0,
    attacking: false,
    attackKind: null,
    attackElapsed: 0,
    attackResolved: false,
    blocking: false,
    blockHeldTicks: 0,
    stagger: 0,
    dodging: false,
    dodgeElapsed: 0,
    dodgeDirX: 0,
    dodgeDirZ: 1,
    lastHitTick: -1000,
    lastHitOutcome: null,
    respawnAtTick: null,
    squadId: s.squadId ?? null,
    squadIndex: s.squadIndex ?? 0,
    archetype: s.archetype ?? 'infantry',
    role: s.role ?? null,
    patrolRoute: s.patrolRoute ?? null,
    morale: 0.85,
    routing: false,
    aiCooldown: 0,
    aiBlockTicks: 0,
    aiTargetId: null,
    aiWaypoint: s.routeStart ?? 0,
    aiChamber: 0,
  };
}

export function createGame(seed = 1): GameState {
  const friendlyThrone = thronePosition('friendly');
  const enemyThrone = thronePosition('enemy');
  const spawns: SpawnSpec[] = [
    {
      id: 'agent',
      kind: 'agent',
      team: 'friendly',
      brain: 'player',
      profile: 'agent',
      x: -150,
      z: -6,
      heading: Math.PI / 2,
      maxHealth: 180,
      baseSpeed: 5.2,
    },
    {
      id: 'king',
      kind: 'king',
      team: 'friendly',
      brain: 'player',
      profile: 'king',
      x: friendlyThrone.x,
      z: friendlyThrone.z,
      heading: Math.PI / 2,
      maxHealth: 260,
      baseSpeed: 2.9,
    },
    {
      id: 'enemyKing',
      kind: 'king',
      team: 'enemy',
      brain: 'royal',
      profile: 'king',
      x: enemyThrone.x,
      z: enemyThrone.z,
      heading: -Math.PI / 2,
      maxHealth: 260,
      baseSpeed: 2.9,
    },
    {
      id: 'enemyAgent',
      kind: 'agent',
      team: 'enemy',
      brain: 'assassin',
      profile: 'agent',
      x: 185,
      z: 16,
      heading: -Math.PI / 2,
      maxHealth: 180,
      baseSpeed: 5.2,
    },
    // practice dummy on the open field, out of range of the wall archers
    {
      id: 'training1',
      kind: 'training',
      team: 'enemy',
      brain: 'training',
      profile: 'trainingEnemy',
      x: -118,
      z: -40,
      heading: -Math.PI / 2,
      maxHealth: 180,
      baseSpeed: 3.4,
    },
  ];

  for (const team of ['friendly', 'enemy'] as Team[]) {
    guardPosts(team).forEach((post, i) => {
      spawns.push({
        id: `${team[0]}guard${i}`,
        kind: 'soldier',
        team,
        brain: 'guard',
        profile: post.profile,
        x: post.x,
        z: post.z,
        elev: post.elev,
        heading: team === 'friendly' ? Math.PI / 2 : -Math.PI / 2,
        maxHealth: post.maxHealth,
        baseSpeed: 3.6,
        archetype: post.archetype,
        role: post.role,
        patrolRoute: post.route,
        routeStart: post.routeStart,
        squadIndex: i,
      });
    });
  }

  const squads: Record<string, ReturnType<typeof makeSquad>> = {};
  for (const team of ['friendly', 'enemy'] as Team[]) {
    for (let q = 0; q < SQUADS_PER_SIDE; q++) {
      const squad = makeSquad(team, q);
      squads[squad.id] = squad;
      for (let i = 0; i < SQUAD_SIZE; i++) {
        spawns.push({
          id: `${team[0]}${q}-${i}`,
          kind: 'soldier',
          team,
          brain: 'soldier',
          profile: 'soldier',
          x: squad.anchorX + ((i % 5) - 2) * 1.7,
          z: squad.anchorZ - Math.floor(i / 5) * 1.7,
          heading: squad.facing,
          maxHealth: 90,
          baseSpeed: 3.3,
          squadId: squad.id,
          squadIndex: i,
        });
      }
    }
  }

  const characters: Record<CharacterId, CharacterState> = {};
  for (const s of spawns) characters[s.id] = makeCharacter(s);

  return {
    tick: 0,
    rngState: seed | 0,
    characters,
    squads,
    controlled: 'agent',
    result: 'playing',
    gold: { friendly: GOLD_START, enemy: GOLD_START },
    goldIncome: { friendly: BASE_INCOME, enemy: BASE_INCOME },
    research: { friendly: [], enemy: [] },
    lastKingAlertTick: { friendly: -1000, enemy: -1000 },
    alarmUntil: { friendly: -1, enemy: -1 },
    posture: { friendly: 'defend', enemy: 'defend' },
    buildings: [],
    projectiles: [],
    nextId: 1,
    events: [],
  };
}

export function tryResearch(state: GameState, team: Team, id: string): boolean {
  const tech = techById(id);
  if (!tech || !isTechAvailable(state, team, id)) return false;
  if (state.gold[team] < tech.cost) return false;
  state.gold[team] -= tech.cost;
  state.research[team].push(id);
  recomputeIncome(state, team);
  if (id === 'bodyguards') {
    for (const post of bodyguardPosts(team)) {
      const cid = `bg${state.nextId++}`;
      state.characters[cid] = makeCharacter({
        id: cid,
        kind: 'soldier',
        team,
        brain: 'guard',
        profile: 'royalGuard',
        x: post.x,
        z: post.z,
        heading: team === 'friendly' ? Math.PI / 2 : -Math.PI / 2,
        maxHealth: 150,
        baseSpeed: 3.6,
      });
    }
  }
  const throne = thronePosition(team);
  state.events.push({ type: 'research', x: throne.x, z: throne.z });
  return true;
}

export function tryPurchase(state: GameState, team: Team, kind: PurchaseKind): boolean {
  const item = SHOP[kind];

  if (item.kind === 'building') {
    const cost = buildingCost(state, team, item.building);
    if (state.gold[team] < cost) return false;
    if (!waveUnlocked(state, team, kind)) return false;
    // one smithy/range/chapel/treasury each; at most two housing
    const count = state.buildings.filter(
      (b) => b.team === team && b.kind === item.building
    ).length;
    if (SINGLETON_BUILDINGS.includes(item.building) && count >= 1) return false;
    if (item.building === 'housing' && count >= HOUSING_MAX) return false;
    const taken = new Set(
      state.buildings
        .filter((b) => b.team === team && b.plot === item.where)
        .map((b) => b.plotIndex)
    );
    const plots = item.where === 'bailey' ? baileyPlots(team) : buildingPlots(team);
    const plotIndex = plots.findIndex((_, i) => !taken.has(i));
    if (plotIndex === -1) return false;
    state.gold[team] -= cost;
    state.buildings.push({
      id: `b${state.nextId++}`,
      team,
      kind: item.building,
      plotIndex,
      plot: item.where,
      progress: 0,
      hp: 1,
      maxHp: item.maxHp,
    });
    state.events.push({ type: 'build', x: plots[plotIndex].x, z: plots[plotIndex].z });
    return true;
  }

  // troop wave — no cooldown: gold and pop cap are the only gates
  if (!waveUnlocked(state, team, kind)) return false;
  if (state.gold[team] < item.cost) return false;
  if (alivePop(state, team) + item.count > popCap(state, team)) return false;
  const muster = musterPosition(team);
  const squads = Object.values(state.squads)
    .filter((q) => q.team === team)
    .sort(
      (a, b) =>
        Object.values(state.characters).filter((c) => c.alive && c.squadId === a.id).length -
        Object.values(state.characters).filter((c) => c.alive && c.squadId === b.id).length
    );
  for (let i = 0; i < item.count; i++) {
    const squad = squads[i % squads.length];
    const squadIndex = Object.values(state.characters).filter(
      (c) => c.squadId === squad.id
    ).length;
    const id = `w${state.nextId++}`;
    state.characters[id] = makeCharacter({
      id,
      kind: 'soldier',
      team,
      brain: 'soldier',
      profile: item.profile,
      x: muster.x,
      z: muster.z + ((i % 3) - 1) * 1.8,
      heading: team === 'friendly' ? Math.PI / 2 : -Math.PI / 2,
      maxHealth: Math.round(item.maxHealth * spawnHealthMultiplier(state, team)),
      baseSpeed: item.baseSpeed,
      squadId: squad.id,
      squadIndex,
      archetype: item.archetype,
    });
  }
  state.gold[team] -= item.cost;
  return true;
}

/** Enemy build order: a real base progression — smithy and farms first,
 * housing to grow the army, then the tech buildings — with waves bought from
 * whatever surplus is left. */
function enemyBuyPolicy(state: GameState): void {
  const count = (kind: BuildingKind) =>
    state.buildings.filter((b) => b.team === 'enemy' && b.kind === kind).length;
  const wants: [BuildingKind, boolean][] = [
    ['smithy', count('smithy') < 1],
    ['farm', count('farm') < 2],
    ['housing', count('housing') < 1],
    ['range', count('range') < 1],
    ['farm', count('farm') < 3],
    ['chapel', count('chapel') < 1],
    ['treasury', count('treasury') < 1],
    ['housing', count('housing') < 2],
    ['market', count('market') < 2],
  ];
  for (const [kind, wanted] of wants) {
    if (!wanted) continue;
    if (state.gold.enemy >= buildingCost(state, 'enemy', kind) + 60) {
      tryPurchase(state, 'enemy', kind);
    }
    break; // save for the next thing on the list rather than skipping ahead
  }
  const waves: PurchaseKind[] = ['infantry', 'archers', 'infantry', 'knights'];
  const pick = waves[Math.floor(state.tick / (30 * TICK_RATE)) % waves.length];
  const item = SHOP[pick];
  if (state.gold.enemy >= item.cost + 150) {
    if (!tryPurchase(state, 'enemy', pick)) tryPurchase(state, 'enemy', 'infantry');
  }
}

function stepProjectiles(state: GameState): void {
  let write = 0;
  const list = state.projectiles;
  for (let i = 0; i < list.length; i++) {
    const a = list[i];
    a.x += a.vx * DT;
    a.z += a.vz * DT;
    a.y += a.vy * DT;
    a.vy -= ARROW_GRAVITY * DT;
    a.ttl -= DT;
    if (a.grace > 0) a.grace -= DT;
    let dead =
      a.ttl <= 0 || a.y < -0.5 || (a.grace <= 0 && arrowBlockedAt(a.x, a.z, a.y));
    if (!dead) {
      for (const target of Object.values(state.characters)) {
        if (!target.alive || target.team === a.team) continue;
        if (Math.abs(target.x - a.x) > 0.7 || Math.abs(target.z - a.z) > 0.7) continue;
        if (a.y < target.elev + 0.1 || a.y > target.elev + 1.9) continue;
        if (Math.hypot(target.x - a.x, target.z - a.z) <= 0.7) {
          const damage = a.damage * defenseMultiplier(state, target);
          receiveArrowHit(target, damage, a.x - a.vx, a.z - a.vz, state.tick, state.events);
          scheduleRespawn(state, target);
          dead = true;
          break;
        }
      }
    }
    if (!dead) list[write++] = a;
  }
  list.length = write;
}

/** Advance the sim one tick. MUTATES state (and returns it) — the per-tick
 * structuredClone was the main scaling cost; snapshot explicitly if you need
 * history (render interpolation copies just position/heading in the shell). */
// The sim keeps running after the match is decided — the crown has fallen,
// but the world lives on and the player can keep roaming (Enter restarts).
export function step(state: GameState, playerCmd: Command): GameState {
  state.tick += 1;
  state.events.length = 0;
  syncBuildingObstacles(state);

  if (playerCmd.switchTo) {
    const target = state.characters[playerCmd.switchTo];
    const isLeader =
      target?.squadId != null &&
      target.team === 'friendly' &&
      squadLeader(state, target.squadId)?.id === target.id;
    if (target && target.alive && (target.brain === 'player' || isLeader)) {
      state.controlled = playerCmd.switchTo;
    }
  }

  // posture + the commander layer: the player states intent, the commander
  // turns it into per-squad objectives (and reacts to enemy movements)
  if (playerCmd.armyPosture && playerCmd.armyPosture !== state.posture.friendly) {
    state.posture.friendly = playerCmd.armyPosture;
    state.events.push({ type: 'horn', x: -95, z: 0, team: 'friendly' });
    tickCommander(state, 'friendly');
  }
  if (state.tick % (5 * TICK_RATE) === 0 && state.tick > 0) enemyPosturePolicy(state);
  if (state.tick % COMMANDER_PERIOD === 0) tickCommander(state, 'friendly');
  if (state.tick % COMMANDER_PERIOD === Math.floor(COMMANDER_PERIOD / 2)) {
    tickCommander(state, 'enemy');
  }
  for (const squad of Object.values(state.squads)) {
    // a player-controlled leader drags the whole squad with them
    const leader = squad.team === 'friendly' ? squadLeader(state, squad.id) : null;
    if (leader && leader.id === state.controlled) {
      squad.anchorX = leader.x;
      squad.anchorZ = leader.z;
      squad.laneZ = leader.z;
      squad.facing = leader.heading;
      continue;
    }
    tickSquad(state, squad, DT);
  }

  // economy: income (buildings + techs + banking), purchases, research
  for (const team of ['friendly', 'enemy'] as Team[]) {
    const income = state.goldIncome[team] + bankingBonus(state, team);
    state.gold[team] = Math.min(GOLD_CAP, state.gold[team] + income * DT);
  }
  if (playerCmd.buy) tryPurchase(state, 'friendly', playerCmd.buy);
  if (playerCmd.research) tryResearch(state, 'friendly', playerCmd.research);
  if (state.tick % 120 === 0) enemyBuyPolicy(state);
  if (state.tick % (35 * TICK_RATE) === 0 && state.tick > 0) enemyResearchPolicy(state);

  // construction progresses; razed buildings collapse out of the list
  let removed = false;
  for (const b of state.buildings) {
    if (b.hp <= 0) {
      removed = true;
      const p = plotOf(b.team, b);
      state.events.push({ type: 'raze', x: p.x, z: p.z, team: b.team });
      continue;
    }
    if (b.progress < 1) {
      b.progress = Math.min(1, b.progress + DT / BUILD_SECONDS);
      b.hp = Math.min(b.maxHp, b.hp + (b.maxHp * DT) / BUILD_SECONDS);
      if (b.progress >= 1) {
        recomputeIncome(state, b.team);
        const p = plotOf(b.team, b);
        state.events.push({ type: 'build', x: p.x, z: p.z, team: b.team });
      }
    }
  }
  if (removed) {
    const razedTeams = new Set(state.buildings.filter((b) => b.hp <= 0).map((b) => b.team));
    state.buildings = state.buildings.filter((b) => b.hp > 0);
    for (const team of razedTeams) recomputeIncome(state, team);
  }

  // royal physician: slow king regeneration
  for (const team of ['friendly', 'enemy'] as Team[]) {
    if (!hasTech(state, team, 'physician')) continue;
    const king = team === 'friendly' ? state.characters.king : state.characters.enemyKing;
    if (king.alive && king.health < king.maxHealth) {
      king.health = Math.min(king.maxHealth, king.health + 1 * DT);
    }
  }

  for (const id of Object.keys(state.characters)) {
    const c = state.characters[id];
    if (!c.alive) {
      handleRespawn(state, c);
      continue;
    }
    const cmd = commandFor(state, c, playerCmd);
    const fromX = c.x;
    const fromZ = c.z;
    stepCharacter(state, c, cmd);
    settleMove(c, fromX, fromZ);
    c.safeX = c.x;
    c.safeZ = c.z;
  }

  stepProjectiles(state);

  // deaths shake the nerve of nearby comrades (soldiers only)
  for (const e of state.events) {
    if (e.type !== 'death' || !e.team) continue;
    for (const c of Object.values(state.characters)) {
      if (!c.alive || c.brain !== 'soldier' || c.team !== e.team) continue;
      if (Math.abs(c.x - e.x) > 10 || Math.abs(c.z - e.z) > 10) continue;
      if (Math.hypot(c.x - e.x, c.z - e.z) < 10) {
        c.morale = Math.max(0, c.morale - 0.12);
      }
    }
  }

  applySeparation(Object.values(state.characters));
  for (const c of Object.values(state.characters)) {
    if (!c.alive) continue;
    resolveObstacles(c);
    c.x = clampX(c.x);
    c.z = clampZ(c.z);
    enforceLevel(c);
  }

  raiseAlarms(state);
  raiseKingAlerts(state);
  applyDebug(state, playerCmd);
  resolveResult(state);
  return state;
}

/** Ring a castle's bell while hostiles are near it; the garrison stays roused
 * (off-duty guards arm up) until the alarm lapses. */
function raiseAlarms(state: GameState): void {
  if (state.tick % 15 !== 0) return;
  for (const team of ['friendly', 'enemy'] as Team[]) {
    const sign = team === 'friendly' ? -1 : 1;
    let danger = false;
    for (const c of Object.values(state.characters)) {
      if (!c.alive || c.team === team) continue;
      if (c.x * sign > 140 && Math.abs(c.z) < 78) {
        danger = true;
        break;
      }
    }
    if (danger) {
      const rising = state.tick >= state.alarmUntil[team];
      state.alarmUntil[team] = state.tick + 12 * TICK_RATE;
      if (rising) state.events.push({ type: 'alarm', x: sign * 163, z: 0 });
    }
  }
}

/** Warn the player when their King is struck or an enemy closes in
 * (rate-limited; the event drives the HUD banner and alarm). */
function raiseKingAlerts(state: GameState): void {
  const king = state.characters.king;
  if (!king.alive) return;
  let danger = king.lastHitTick === state.tick;
  if (!danger && state.tick % 30 === 0) {
    for (const other of Object.values(state.characters)) {
      if (!other.alive || other.team === 'friendly') continue;
      if (Math.hypot(other.x - king.x, other.z - king.z) < 14) {
        danger = true;
        break;
      }
    }
  }
  if (danger && state.tick - state.lastKingAlertTick.friendly > 6 * TICK_RATE) {
    state.lastKingAlertTick.friendly = state.tick;
    state.events.push({ type: 'king-threat', x: king.x, z: king.z });
  }
}

/** Enemy research: a sensible priority order; availability (prereqs and
 * standing buildings) is enforced by tryResearch, so razing the enemy smithy
 * genuinely stalls their military tech. */
function enemyResearchPolicy(state: GameState): void {
  const order = [
    'taxation',
    'weapon-drills',
    'trade-routes',
    'shields',
    'crop-rotation',
    'longbows',
    'unlock-knights',
    'assassin-training',
    'veterans',
    'bodyguards',
    'fortifications',
    'banking',
    'steel-doctrine',
    'physician',
  ];
  for (const id of order) {
    if (hasTech(state, 'enemy', id)) continue;
    if (tryResearch(state, 'enemy', id)) return;
  }
}

function commandFor(state: GameState, c: CharacterState, playerCmd: Command): Command {
  if (c.id === state.controlled) return playerCmd;
  tickAiMemory(c);
  switch (c.brain) {
    case 'training':
      return trainingCommand(state, c);
    case 'soldier':
      return soldierCommand(state, c);
    case 'guard':
      return guardCommand(state, c);
    case 'royal':
      return royalCommand(state, c);
    case 'assassin':
      return assassinCommand(state, c);
    case 'player':
      // the avatar you're NOT controlling defends itself where it stands
      return autoDefendCommand(state, c);
  }
  return EMPTY_COMMAND;
}

function stepCharacter(state: GameState, c: CharacterState, cmd: Command): void {
  const p = PROFILES[c.profile];

  if (c.stagger > 0) c.stagger = Math.max(0, c.stagger - DT);

  // dodge in progress: locked in, nothing else happens this tick
  if (c.dodging) {
    c.dodgeElapsed += DT;
    const speed = c.baseSpeed * DODGE_SPEED_MULTIPLIER;
    c.x = clampX(c.x + c.dodgeDirX * speed * DT);
    c.z = clampZ(c.z + c.dodgeDirZ * speed * DT);
    c.moving = true;
    c.sprinting = false;
    if (c.dodgeElapsed >= DODGE_DURATION) {
      c.dodging = false;
      c.dodgeElapsed = 0;
    }
    tickStamina(c, DT);
    return;
  }

  // feint: block input during a heavy's early windup cancels it
  if (c.attacking && c.attackKind === 'heavy' && cmd.block && !c.exhausted) {
    const spec = attackSpec(c.profile, 'heavy');
    if (c.attackElapsed < spec.windup * FEINT_WINDOW_FRACTION) {
      trySpendStamina(c, FEINT_STAMINA_COST);
      c.attacking = false;
      c.attackKind = null;
      c.attackElapsed = 0;
      c.attackResolved = false;
    }
  }

  // blocking
  const wasBlocking = c.blocking;
  c.blocking = cmd.block && !c.attacking && c.stagger <= 0 && !c.exhausted;
  if (c.blocking) {
    c.blockHeldTicks = wasBlocking ? c.blockHeldTicks + 1 : 0;
    if (cmd.faceYaw !== null) c.heading = cmd.faceYaw;
    if (!trySpendStamina(c, p.blockStaminaDrainPerSecond * DT)) {
      c.blocking = false;
      c.blockHeldTicks = 0;
    }
  } else {
    c.blockHeldTicks = 0;
  }

  // attack start
  const startKind: AttackKind | null = cmd.attackLight
    ? 'light'
    : cmd.attackHeavy
      ? 'heavy'
      : cmd.kick
        ? 'kick'
        : null;
  if (startKind && !c.attacking && !c.blocking && c.stagger <= 0 && !c.exhausted) {
    const spec = attackSpec(c.profile, startKind);
    if (trySpendStamina(c, spec.staminaCost)) {
      c.attacking = true;
      c.attackKind = startKind;
      c.attackElapsed = 0;
      c.attackResolved = false;
      if (cmd.faceYaw !== null) c.heading = cmd.faceYaw;
    }
  }

  // attack progress: single hit resolution at the end of windup
  if (c.attacking && c.attackKind) {
    const spec = attackSpec(c.profile, c.attackKind);
    c.attackElapsed += DT;
    if (!c.attackResolved && c.attackElapsed >= spec.windup) {
      c.attackResolved = true;
      state.events.push({
        type: c.attackKind === 'kick' ? 'kick' : c.attackKind === 'heavy' ? 'swing-heavy' : 'swing',
        x: c.x,
        z: c.z,
      });
      resolveAttackHitbox(state, c, c.attackKind);
    }
    if (c.attackElapsed >= spec.windup + spec.recovery) {
      c.attacking = false;
      c.attackKind = null;
      c.attackElapsed = 0;
    }
  }

  // archer shot
  if (
    cmd.shoot &&
    c.archetype === 'archer' &&
    !c.attacking &&
    !c.blocking &&
    c.stagger <= 0 &&
    !c.exhausted
  ) {
    if (cmd.faceYaw !== null) c.heading = cmd.faceYaw;
    const fx = Math.sin(c.heading);
    const fz = Math.cos(c.heading);
    state.projectiles.push({
      x: c.x + fx * 0.9,
      z: c.z + fz * 0.9,
      y: c.elev + ARROW_LAUNCH_H,
      vx: fx * ARROW_SPEED,
      vz: fz * ARROW_SPEED,
      vy: cmd.shootVy ?? 1.2,
      team: c.team,
      damage: 13 * offenseMultiplier(state, c),
      ttl: 1.8,
      grace: 0.09,
    });
    state.events.push({ type: 'arrow', x: c.x, z: c.z });
  }

  // dodge start (cancels block, never an attack)
  if (cmd.dodge && !c.attacking && !c.dodging && c.stagger <= 0 && !c.exhausted) {
    if (trySpendStamina(c, DODGE_STAMINA_COST * dodgeCostMultiplier(state, c))) {
      c.dodging = true;
      c.dodgeElapsed = 0;
      c.blocking = false;
      c.blockHeldTicks = 0;
      const len = Math.hypot(cmd.moveX, cmd.moveZ);
      if (len > 0.001) {
        c.dodgeDirX = cmd.moveX / len;
        c.dodgeDirZ = cmd.moveZ / len;
      } else {
        c.dodgeDirX = -Math.sin(c.heading);
        c.dodgeDirZ = -Math.cos(c.heading);
      }
      c.moving = true;
      c.sprinting = false;
      state.events.push({ type: 'dodge', x: c.x, z: c.z });
      tickStamina(c, DT);
      return;
    }
  }

  // movement
  let multiplier = 1;
  if (c.stagger > 0) multiplier = 0;
  else {
    if (c.blocking) multiplier *= p.blockMoveMultiplier;
    if (c.attacking) multiplier *= p.attackMoveMultiplier;
    if (c.exhausted) multiplier *= p.exhaustedMoveMultiplier;
  }

  const len = Math.hypot(cmd.moveX, cmd.moveZ);
  c.moving = len > 0.001 && multiplier > 0;
  c.sprinting = c.moving && cmd.sprint && canSprint(c);
  if (c.sprinting && !trySpendStamina(c, p.sprintStaminaCostPerSecond * DT)) {
    c.sprinting = false;
  }
  if (c.moving) {
    const speed = c.baseSpeed * multiplier * (c.sprinting ? SPRINT_MULTIPLIER : 1);
    c.x = clampX(c.x + (cmd.moveX / len) * speed * DT);
    c.z = clampZ(c.z + (cmd.moveZ / len) * speed * DT);
    if (!c.blocking && !c.attacking) c.heading = Math.atan2(cmd.moveX, cmd.moveZ);
  } else if (cmd.faceYaw !== null && !c.attacking && !c.blocking && c.brain !== 'player') {
    // idle AI turns to face its point of interest (formation facing, target)
    c.heading = cmd.faceYaw;
  }

  tickStamina(c, DT);
}

function clampX(x: number): number {
  return Math.max(-WORLD_HALF_X, Math.min(WORLD_HALF_X, x));
}

function clampZ(z: number): number {
  return Math.max(-WORLD_HALF_Z, Math.min(WORLD_HALF_Z, z));
}

function resolveAttackHitbox(state: GameState, attacker: CharacterState, kind: AttackKind): void {
  const spec = attackSpec(attacker.profile, kind);
  const fx = Math.sin(attacker.heading);
  const fz = Math.cos(attacker.heading);
  const centerX = attacker.x + fx * spec.range * 0.55;
  const centerZ = attacker.z + fz * spec.range * 0.55;

  for (const target of Object.values(state.characters)) {
    if (target.id === attacker.id || !target.alive || target.team === attacker.team) continue;
    if (Math.abs(target.elev - attacker.elev) > 1.6) continue; // swords don't reach up walls
    // cheap reject before the exact overlap test
    if (Math.abs(target.x - centerX) > 3 || Math.abs(target.z - centerZ) > 3) continue;
    const overlap =
      Math.hypot(target.x - centerX, target.z - centerZ) <= spec.radius + BODY_RADIUS;
    if (!overlap) continue;
    if (!isInsideArc(attacker.heading, target.x - attacker.x, target.z - attacker.z, spec.arc)) {
      continue;
    }
    const damage =
      spec.damage * offenseMultiplier(state, attacker) * defenseMultiplier(state, target);
    const outcome = receiveHit(target, attacker, kind, damage, state.tick, state.events);
    scheduleRespawn(state, target);
    if (outcome === 'parried') return;
  }

  // enemy buildings are fair game for any swing (raiders live on this)
  if (kind !== 'kick' && attacker.elev < 2) {
    for (const b of state.buildings) {
      if (b.team === attacker.team || b.hp <= 0) continue;
      const p = plotOf(b.team, b);
      if (Math.hypot(p.x - centerX, p.z - centerZ) > spec.radius + 3.2) continue;
      b.hp -= spec.damage * offenseMultiplier(state, attacker);
      state.events.push({ type: 'hit', x: p.x, z: p.z });
    }
  }
}

function handleRespawn(state: GameState, c: CharacterState): void {
  if (c.respawnAtTick === null || state.tick < c.respawnAtTick) return;
  c.respawnAtTick = null;
  c.alive = true;
  c.health = c.maxHealth;
  c.stamina = PROFILES[c.profile].maximumStamina;
  c.exhausted = false;
  c.regenDelay = 0;
  c.x = c.spawnX;
  c.z = c.spawnZ;
  c.elev = c.spawnElev;
  c.safeX = c.spawnX;
  c.safeZ = c.spawnZ;
  c.morale = 0.85;
  c.routing = false;
  c.lastHitOutcome = null;
  c.aiTargetId = null;
  c.aiWaypoint = 0;
  resetCombatState(c);
  state.events.push({ type: 'respawn', x: c.x, z: c.z });
}

function applyDebug(state: GameState, cmd: Command): void {
  if (cmd.debug === 'damageControlled') {
    const c = state.characters[state.controlled];
    damage(c, 10);
    c.lastHitTick = state.tick;
    scheduleRespawn(state, c);
  } else if (cmd.debug === 'forceDefeat') {
    const king = state.characters.king;
    king.health = 0;
    king.alive = false;
  } else if (cmd.debug === 'forceVictory') {
    state.result = 'victory';
  }
}

export function damage(c: CharacterState, amount: number): void {
  if (!c.alive) return;
  c.health = Math.max(0, c.health - amount);
  if (c.health <= 0) {
    c.alive = false;
    resetCombatState(c);
  }
}

function resolveResult(state: GameState): void {
  // the first decisive moment fixes the result; later deaths don't change it
  if (state.result === 'playing') {
    const myKingDead = !state.characters.king.alive;
    const enemyKingDead = !state.characters.enemyKing.alive;
    if (myKingDead && enemyKingDead) state.result = 'draw';
    else if (enemyKingDead) state.result = 'victory';
    else if (myKingDead) state.result = 'defeat';
  }
  const c = state.characters[state.controlled];
  if (!c.alive) {
    const fallback = Object.values(state.characters).find((o) => o.alive && o.brain === 'player');
    if (fallback) state.controlled = fallback.id;
  }
}
